import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { CONDITION_OPERATOR, TConditionOperator, TTemplateFieldType } from '../../models';
import { TAddCustomFieldInput } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '440px' },
  ruleRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalS },
});

const OPERATOR_LABELS: { value: TConditionOperator; label: string }[] = [
  { value: CONDITION_OPERATOR.EQUALS, label: 'equals' },
  { value: CONDITION_OPERATOR.NOT_EQUALS, label: 'does not equal' },
  { value: CONDITION_OPERATOR.CONTAINS, label: 'contains' },
];

type TSection = { id: string; title: string };
type TConditionSource = { key: string; label: string };

type TProps = {
  open: boolean;
  fieldType?: TTemplateFieldType;
  fieldTypeLabel?: string;
  sections: TSection[];
  conditionSources: TConditionSource[];
  onClose: () => void;
  onAdd: (input: TAddCustomFieldInput) => void;
};

// The compact `Add custom field` dialog (prompt Part 3). Only the essentials:
// label, section, required, an optional default/suggested value and one simple
// visibility rule. It never edits an active assessment; it extends the template.
export const AddCustomFieldDialog = ({
  open,
  fieldType,
  fieldTypeLabel,
  sections,
  conditionSources,
  onClose,
  onAdd,
}: TProps) => {
  const classes = useStyles();
  const [label, setLabel] = useState('');
  const [sectionId, setSectionId] = useState<string[]>([]);
  const [required, setRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [showConditionally, setShowConditionally] = useState(false);
  const [sourceKey, setSourceKey] = useState<string[]>([]);
  const [operator, setOperator] = useState<TConditionOperator>(CONDITION_OPERATOR.EQUALS);
  const [conditionValue, setConditionValue] = useState('');

  useEffect(() => {
    if (open) {
      setLabel('');
      setSectionId(sections[0] ? [sections[0].id] : []);
      setRequired(false);
      setDefaultValue('');
      setShowConditionally(false);
      setSourceKey([]);
      setOperator(CONDITION_OPERATOR.EQUALS);
      setConditionValue('');
    }
  }, [open, sections]);

  const conditionOk = !showConditionally || (sourceKey.length === 1 && Boolean(conditionValue.trim()));
  const canAdd = Boolean(label.trim()) && sectionId.length === 1 && conditionOk && Boolean(fieldType);

  const handleAdd = () => {
    if (!canAdd || !fieldType) return;
    onAdd({
      label: label.trim(),
      fieldType,
      sectionId: sectionId[0],
      required,
      prefillKind: defaultValue.trim() ? 'templateDefault' : 'none',
      prefilledValue: defaultValue.trim() || undefined,
      visibleWhen:
        showConditionally && sourceKey[0]
          ? { sourceKey: sourceKey[0], operator, value: conditionValue.trim() }
          : undefined,
    });
  };

  const sectionLabel = sections.find((section) => section.id === sectionId[0])?.title ?? '';
  const sourceLabel = conditionSources.find((source) => source.key === sourceKey[0])?.label ?? '';
  const operatorLabel = OPERATOR_LABELS.find((entry) => entry.value === operator)?.label ?? '';

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />
              </DialogTrigger>
            }
          >
            Add custom field{fieldTypeLabel ? ` · ${fieldTypeLabel}` : ''}
          </DialogTitle>
          <DialogContent>
            <div className={classes.body}>
              <Field label="Field label" required>
                <Input
                  value={label}
                  onChange={(_, data) => setLabel(data.value)}
                  placeholder="e.g. Second-source status"
                />
              </Field>

              <Field label="Assessment section" required>
                <Dropdown
                  selectedOptions={sectionId}
                  value={sectionLabel}
                  placeholder="Select a section"
                  onOptionSelect={(_, data) => setSectionId(data.selectedOptions)}
                >
                  {sections.map((section) => (
                    <Option key={section.id} value={section.id} text={section.title}>
                      {section.title}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <Switch checked={required} label="Required" onChange={(_, data) => setRequired(data.checked)} />

              <Field label="Default or suggested value (optional)">
                <Input
                  value={defaultValue}
                  onChange={(_, data) => setDefaultValue(data.value)}
                  placeholder="Optional prefill"
                />
              </Field>

              <Switch
                checked={showConditionally}
                label="Show conditionally"
                onChange={(_, data) => setShowConditionally(data.checked)}
              />

              {showConditionally ? (
                <>
                  <div className={classes.ruleRow}>
                    <Field label="Show when">
                      <Dropdown
                        selectedOptions={sourceKey}
                        value={sourceLabel}
                        placeholder="Field or question"
                        onOptionSelect={(_, data) => setSourceKey(data.selectedOptions)}
                      >
                        {conditionSources.map((source) => (
                          <Option key={source.key} value={source.key} text={source.label}>
                            {source.label}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Operator">
                      <Dropdown
                        selectedOptions={[operator]}
                        value={operatorLabel}
                        onOptionSelect={(_, data) => setOperator(data.optionValue as TConditionOperator)}
                      >
                        {OPERATOR_LABELS.map((entry) => (
                          <Option key={entry.value} value={entry.value} text={entry.label}>
                            {entry.label}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  </div>
                  <Field label="Value">
                    <Input
                      value={conditionValue}
                      onChange={(_, data) => setConditionValue(data.value)}
                      placeholder="e.g. Yes"
                    />
                  </Field>
                </>
              ) : null}

              <MessageBar intent="info">
                <MessageBarBody>Template changes apply to newly created Changes.</MessageBarBody>
              </MessageBar>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!canAdd} onClick={handleAdd}>
              Add field
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
